import type { SecondOrderDomainKey } from "@akem/shared";
import { getProfileSocketEndpoint } from "../config/api.js";

export interface IntelligenceLaneTimeline {
  artifactCreatedAt: string;
  normalizedAt: string;
  reportReadyAt: string;
  widgetPublishedAt: string;
  deliveredAt: string;
  durationsMs: {
    ingestToNormalize: number;
    normalizeToWidget: number;
    widgetToDelivery: number;
  };
}

export interface IntelligenceLaneUpdate {
  profileId: string;
  domain: string;
  widgetType: string;
  items: unknown[];
  artifactId?: string;
  instrumentId?: string;
  reportId?: string;
  artifactTs?: number | null;
  reportGeneratedAt?: string | null;
  dispatchedAt?: string | null;
  timeline?: IntelligenceLaneTimeline | null;
  fanoutProfiles?: number | null;
}

export type IntelligenceLaneHandler = (update: IntelligenceLaneUpdate) => void;

export interface IntelligenceIngressUpdate {
  profileId: string;
  domain: string;
  instrumentId: string;
  artifactId: string;
  ts?: number;
  reportCacheKey?: string | null;
}

export type IntelligenceIngressHandler = (
  update: IntelligenceIngressUpdate
) => void;

export interface IntelligenceMonitorEvent {
  profileId: string;
  instrumentId: string;
  storylineId: string;
  title: string;
  summary: string;
  storylineTitle?: string;
  ts: number;
}

export type IntelligenceMonitorHandler = (
  update: IntelligenceMonitorEvent
) => void;

export type ChatEvent =
  | { type: "text-delta"; text: string }
  | { type: "tool-call"; toolCallId: string; toolName: string; args: any }
  | { type: "tool-result"; toolCallId: string; toolName: string; result: any }
  | { type: "error"; error: string }
  | { type: "done" };

export type ChatEventHandler = (event: ChatEvent) => void;

type ChatSendPayload = {
  messages: unknown[];
  ticker?: string;
  profileId?: string;
  storylineContext?: unknown;
  token?: string;
};

type SubscriptionOptions = {
  profileIds?: string[];
};

class IntelligenceWidgetsClient {
  private socket: WebSocket | null = null;
  private connectInFlight = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectAttempts = 0;
  private shouldReconnect = false;
  private subscriptionDirty = false;
  private warnedNoEndpoint = false;
  private warnedNoSocket = false;
  private pendingChatMessages: ChatSendPayload[] = [];

  private readonly laneSubscribers = new Map<
    string,
    Set<IntelligenceLaneHandler>
  >();
  private readonly profileRefCounts = new Map<string, number>();
  private readonly ingressSubscribers = new Set<IntelligenceIngressHandler>();
  private readonly monitorSubscribers = new Set<IntelligenceMonitorHandler>();
  private readonly chatSubscribers = new Set<ChatEventHandler>();

  private registerProfiles(profileIds?: string[]): () => void {
    const ids = Array.from(
      new Set(
        (profileIds || [])
          .map((id) => (typeof id === "string" ? id.trim() : ""))
          .filter(Boolean)
      )
    );
    if (ids.length === 0) return () => undefined;

    ids.forEach((id) => {
      const current = this.profileRefCounts.get(id) ?? 0;
      this.profileRefCounts.set(id, current + 1);
    });

    this.ensureConnection();
    this.subscriptionDirty = true;
    this.syncSubscriptions();

    return () => {
      ids.forEach((id) => {
        const nextCount = (this.profileRefCounts.get(id) ?? 1) - 1;
        if (nextCount <= 0) {
          this.profileRefCounts.delete(id);
        } else {
          this.profileRefCounts.set(id, nextCount);
        }
      });
      this.subscriptionDirty = true;
      this.syncSubscriptions();
    };
  }

  subscribeLane(
    profileId: string | undefined,
    domain: SecondOrderDomainKey | string,
    handler: IntelligenceLaneHandler
  ): () => void {
    if (!profileId || !domain || typeof handler !== "function") {
      return () => undefined;
    }

    const key = this.makeLaneKey(profileId, domain);
    let listeners = this.laneSubscribers.get(key);
    if (!listeners) {
      listeners = new Set();
      this.laneSubscribers.set(key, listeners);
    }
    listeners.add(handler);

    const current = this.profileRefCounts.get(profileId) ?? 0;
    this.profileRefCounts.set(profileId, current + 1);

    this.ensureConnection();
    this.subscriptionDirty = true;
    this.syncSubscriptions();

    return () => {
      listeners?.delete(handler);
      if (listeners && listeners.size === 0) {
        this.laneSubscribers.delete(key);
      }

      const nextCount = (this.profileRefCounts.get(profileId) ?? 1) - 1;
      if (nextCount <= 0) {
        this.profileRefCounts.delete(profileId);
      } else {
        this.profileRefCounts.set(profileId, nextCount);
      }

      if (this.profileRefCounts.size === 0) {
        this.subscriptionDirty = false;
        if (
          this.ingressSubscribers.size === 0 &&
          this.monitorSubscribers.size === 0 &&
          this.chatSubscribers.size === 0
        ) {
          this.teardown(false);
        }
      } else {
        this.subscriptionDirty = true;
        this.syncSubscriptions();
      }
    };
  }

  private ensureConnection(): void {
    if (
      this.profileRefCounts.size === 0 &&
      this.ingressSubscribers.size === 0 &&
      this.monitorSubscribers.size === 0 &&
      this.chatSubscribers.size === 0
    ) {
      return;
    }
    if (
      this.socket &&
      (this.socket.readyState === WebSocket.OPEN ||
        this.socket.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }
    if (this.connectInFlight) return;

    const endpoint = getProfileSocketEndpoint();
    if (!endpoint) {
      if (!this.warnedNoEndpoint) {
        console.warn(
          "[IntelligenceWidgets] profile socket endpoint unavailable"
        );
        this.warnedNoEndpoint = true;
      }
      return;
    }

    const SocketCtor =
      typeof window !== "undefined"
        ? window.WebSocket || (window as any).MozWebSocket
        : undefined;
    if (typeof SocketCtor !== "function") {
      if (!this.warnedNoSocket) {
        console.warn(
          "[IntelligenceWidgets] WebSocket API is not available in this environment"
        );
        this.warnedNoSocket = true;
      }
      return;
    }

    try {
      // Help diagnose misconfigured endpoints in production
      try {
        console.warn("[IntelligenceWidgets] connecting to endpoint", {
          endpoint,
        });
      } catch {}
      const socket = new SocketCtor(endpoint);
      this.socket = socket as WebSocket;
      this.connectInFlight = true;
      this.shouldReconnect = true;
      this.reconnectAttempts = 0;

      this.socket.addEventListener("open", this.handleOpen);
      this.socket.addEventListener("close", this.handleClose);
      this.socket.addEventListener("message", this.handleMessage);
      this.socket.addEventListener("error", this.handleError);
    } catch (error) {
      console.warn("[IntelligenceWidgets] failed to open websocket", {
        error,
      });
      this.socket = null;
      this.connectInFlight = false;
      this.shouldReconnect = true;
      this.scheduleReconnect();
    }
  }

  private teardown(expectReconnect: boolean): void {
    if (this.reconnectTimer !== null) {
      globalThis.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.clearHeartbeat();

    this.shouldReconnect = expectReconnect;

    if (this.socket) {
      try {
        this.socket.removeEventListener("open", this.handleOpen);
        this.socket.removeEventListener("close", this.handleClose);
        this.socket.removeEventListener("message", this.handleMessage);
        this.socket.removeEventListener("error", this.handleError);
        if (!expectReconnect && this.socket.readyState === WebSocket.OPEN) {
          this.socket.close(1000, "idle");
        }
      } catch {}
    }

    this.socket = null;
    this.connectInFlight = false;
    if (!expectReconnect) {
      this.shouldReconnect = false;
    }
  }

  private handleOpen = (): void => {
    this.connectInFlight = false;
    this.reconnectAttempts = 0;
    this.subscriptionDirty = true;
    this.syncSubscriptions();
    this.startHeartbeat();
    this.flushPendingChatMessages();
  };

  private handleClose = (event: CloseEvent): void => {
    try {
      console.warn("[IntelligenceWidgets] websocket closed", {
        code: event?.code,
        reason: event?.reason,
        wasClean: event?.wasClean,
      });
    } catch {}
    const hasAnyListeners =
      this.profileRefCounts.size > 0 ||
      this.ingressSubscribers.size > 0 ||
      this.monitorSubscribers.size > 0 ||
      this.chatSubscribers.size > 0;
    const shouldRetry = this.shouldReconnect && hasAnyListeners;
    this.teardown(shouldRetry);
    if (shouldRetry) {
      this.scheduleReconnect();
    }
  };

  private handleError = (event: Event): void => {
    console.warn("[IntelligenceWidgets] websocket error", {
      event,
    });
  };

  private handleMessage = (event: MessageEvent<string>): void => {
    let payload: any;
    try {
      payload = event.data ? JSON.parse(event.data) : null;
    } catch {
      payload = null;
    }
    if (!payload || typeof payload !== "object") return;

    const type = String(payload.type || "").toLowerCase();
    if (type === "intelligenceupdate") {
      this.dispatchIntelligenceUpdate(payload);
      return;
    }

    if (type === "intelligenceingress") {
      this.dispatchIngressUpdate(payload);
      return;
    }

    if (type === "monitorevent") {
      this.dispatchMonitorEvent(payload);
      return;
    }

    if (type === "chat.event") {
      this.dispatchChatEvent(payload);
      return;
    }

    if (type === "subscription") {
      // Subscription acknowledgements are informational only.
      return;
    }

    if (type === "error") {
      console.warn("[IntelligenceWidgets] socket error payload", payload);
      this.dispatchChatEvent(payload);
      return;
    }

    // Chat events (text-delta, tool-call, etc.)
    if (
      type === "text-delta" ||
      type === "tool-call" ||
      type === "tool-result" ||
      type === "done"
    ) {
      this.dispatchChatEvent(payload);
      return;
    }
  };

  private dispatchIntelligenceUpdate(payload: any): void {
    const profileId =
      typeof payload.profileId === "string" ? payload.profileId : "";
    if (!profileId) return;
    const widgetType =
      typeof payload.widgetType === "string" ? payload.widgetType : "";
    const domain =
      typeof payload.domain === "string" && payload.domain
        ? payload.domain
        : widgetType;
    if (!domain) return;

    const items = Array.isArray(payload.items) ? payload.items : [];
    const update: IntelligenceLaneUpdate = {
      profileId,
      domain,
      widgetType: widgetType || domain,
      items,
      artifactId:
        typeof payload.artifactId === "string" ? payload.artifactId : undefined,
      instrumentId:
        typeof payload.instrumentId === "string"
          ? payload.instrumentId
          : undefined,
      reportId:
        typeof payload.reportId === "string" ? payload.reportId : undefined,
      artifactTs:
        typeof payload.artifactTs === "number" &&
        Number.isFinite(payload.artifactTs)
          ? payload.artifactTs
          : undefined,
      reportGeneratedAt:
        typeof payload.reportGeneratedAt === "string"
          ? payload.reportGeneratedAt
          : undefined,
      dispatchedAt:
        typeof payload.dispatchedAt === "string"
          ? payload.dispatchedAt
          : undefined,
      timeline:
        payload.timeline && typeof payload.timeline === "object"
          ? (payload.timeline as IntelligenceLaneTimeline)
          : undefined,
      fanoutProfiles:
        typeof payload.fanoutProfiles === "number" &&
        Number.isFinite(payload.fanoutProfiles)
          ? payload.fanoutProfiles
          : undefined,
    };

    const listeners = this.laneSubscribers.get(
      this.makeLaneKey(profileId, domain)
    );
    if (!listeners || listeners.size === 0) return;

    listeners.forEach((listener) => {
      try {
        listener(update);
      } catch (error) {
        console.error("[IntelligenceWidgets] lane listener failed", error);
      }
    });
  }

  private dispatchIngressUpdate(payload: any): void {
    const profileId =
      typeof payload.profileId === "string" ? payload.profileId : "";
    const domain = typeof payload.domain === "string" ? payload.domain : "";
    const instrumentId =
      typeof payload.instrumentId === "string" ? payload.instrumentId : "";
    const artifactId =
      typeof payload.artifactId === "string" ? payload.artifactId : "";
    if (!profileId || !domain || !instrumentId || !artifactId) return;

    const update: IntelligenceIngressUpdate = {
      profileId,
      domain,
      instrumentId,
      artifactId,
      ts: typeof payload.ts === "number" ? payload.ts : undefined,
      reportCacheKey:
        typeof payload.reportCacheKey === "string" ||
        payload.reportCacheKey === null
          ? (payload.reportCacheKey as string | null)
          : undefined,
    };

    this.ingressSubscribers.forEach((listener) => {
      try {
        listener(update);
      } catch (error) {
        console.error("[IntelligenceWidgets] ingress listener failed", error);
      }
    });
  }

  private dispatchMonitorEvent(payload: any): void {
    const profileId =
      typeof payload.profileId === "string" ? payload.profileId : "";
    const instrumentId =
      typeof payload.instrumentId === "string" ? payload.instrumentId : "";
    const storylineId =
      typeof payload.storylineId === "string" ? payload.storylineId : "";
    if (!profileId || !instrumentId || !storylineId) return;

    const update: IntelligenceMonitorEvent = {
      profileId,
      instrumentId,
      storylineId,
      title: typeof payload.title === "string" ? payload.title : "",
      summary: typeof payload.summary === "string" ? payload.summary : "",
      ts: typeof payload.ts === "number" ? payload.ts : Date.now(),
    };

    this.monitorSubscribers.forEach((listener) => {
      try {
        listener(update);
      } catch (error) {
        console.error("[IntelligenceWidgets] monitor listener failed", error);
      }
    });
  }

  private dispatchChatEvent(payload: any): void {
    const errorText =
      typeof payload.error === "string"
        ? payload.error
        : typeof payload.message === "string"
        ? payload.message
        : undefined;

    const event: ChatEvent = {
      type: payload.type,
      text: payload.text,
      toolCallId: payload.toolCallId,
      toolName: payload.toolName,
      args: payload.args,
      result: payload.result,
      error: errorText as any,
    } as any;

    this.chatSubscribers.forEach((listener) => {
      try {
        listener(event);
      } catch (error) {
        console.error("[IntelligenceWidgets] chat listener failed", error);
      }
    });
  }

  private flushPendingChatMessages(): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }
    if (this.pendingChatMessages.length === 0) {
      return;
    }
    const queue = this.pendingChatMessages;
    this.pendingChatMessages = [];
    for (const payload of queue) {
      try {
        this.socket.send(
          JSON.stringify({
            action: "chat",
            ...payload,
          })
        );
      } catch (error) {
        console.warn("[IntelligenceWidgets] failed to send queued chat", {
          error,
        });
      }
    }
  }

  sendChatMessage(payload: ChatSendPayload): void {
    this.ensureConnection();
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.warn(
        "[IntelligenceWidgets] socket not ready for chat; queuing message"
      );
      this.pendingChatMessages.push(payload);
      return;
    }
    this.socket.send(
      JSON.stringify({
        action: "chat",
        ...payload,
      })
    );
  }

  subscribeChat(handler: ChatEventHandler): () => void {
    if (typeof handler !== "function") return () => undefined;
    this.chatSubscribers.add(handler);
    this.ensureConnection();
    return () => {
      this.chatSubscribers.delete(handler);
      if (
        this.profileRefCounts.size === 0 &&
        this.ingressSubscribers.size === 0 &&
        this.monitorSubscribers.size === 0 &&
        this.chatSubscribers.size === 0
      ) {
        this.teardown(false);
      }
    };
  }

  private syncSubscriptions(): void {
    if (!this.subscriptionDirty) return;
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;

    this.subscriptionDirty = false;
    const profileIds = Array.from(this.profileRefCounts.keys());
    if (profileIds.length === 0) return;

    const message = {
      action: "subscribe",
      mode: "replace",
      profileIds,
      freshnessSec: 120,
    };
    try {
      this.socket.send(JSON.stringify(message));
    } catch (error) {
      console.warn("[IntelligenceWidgets] failed to send subscribe", {
        error,
      });
      this.subscriptionDirty = true;
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null) return;
    if (
      this.profileRefCounts.size === 0 &&
      this.ingressSubscribers.size === 0 &&
      this.monitorSubscribers.size === 0 &&
      this.chatSubscribers.size === 0
    ) {
      return;
    }

    const baseDelay = 1000;
    const factor = Math.min(this.reconnectAttempts, 5);
    const delay = Math.min(baseDelay * Math.pow(2, factor), 30000);
    this.reconnectAttempts += 1;

    this.reconnectTimer = globalThis.setTimeout(() => {
      this.reconnectTimer = null;
      this.ensureConnection();
    }, delay);
  }

  private startHeartbeat(): void {
    this.clearHeartbeat();
    if (!this.socket) return;
    this.heartbeatTimer = globalThis.setInterval(() => {
      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;
      try {
        this.socket.send(JSON.stringify({ action: "ping", at: Date.now() }));
      } catch (error) {
        console.warn("[IntelligenceWidgets] heartbeat send failed", {
          error,
        });
      }
    }, 45000);
  }

  private clearHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      globalThis.clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  subscribeIngress(
    handler: IntelligenceIngressHandler,
    options?: SubscriptionOptions
  ): () => void {
    if (typeof handler !== "function") {
      return () => undefined;
    }
    const releaseProfiles = this.registerProfiles(options?.profileIds);
    this.ingressSubscribers.add(handler);
    this.ensureConnection();
    return () => {
      this.ingressSubscribers.delete(handler);
      releaseProfiles();
      if (
        this.profileRefCounts.size === 0 &&
        this.ingressSubscribers.size === 0 &&
        this.monitorSubscribers.size === 0 &&
        this.chatSubscribers.size === 0
      ) {
        this.teardown(false);
      }
    };
  }

  subscribeMonitor(
    handler: IntelligenceMonitorHandler,
    options?: SubscriptionOptions
  ): () => void {
    if (typeof handler !== "function") {
      return () => undefined;
    }
    const releaseProfiles = this.registerProfiles(options?.profileIds);
    this.monitorSubscribers.add(handler);
    this.ensureConnection();
    return () => {
      this.monitorSubscribers.delete(handler);
      releaseProfiles();
      if (
        this.profileRefCounts.size === 0 &&
        this.ingressSubscribers.size === 0 &&
        this.monitorSubscribers.size === 0 &&
        this.chatSubscribers.size === 0
      ) {
        this.teardown(false);
      }
    };
  }

  private makeLaneKey(
    profileId: string,
    domain: string | SecondOrderDomainKey
  ): string {
    return `${profileId}::${domain}`;
  }
}

const client = new IntelligenceWidgetsClient();

export function subscribeToIntelligenceLane(
  profileId: string | undefined,
  domain: SecondOrderDomainKey,
  handler: IntelligenceLaneHandler
): () => void {
  return client.subscribeLane(profileId, domain, handler);
}

export function subscribeToIntelligenceIngress(
  handler: IntelligenceIngressHandler,
  options?: SubscriptionOptions
): () => void {
  return client.subscribeIngress(handler, options);
}

export function subscribeToIntelligenceMonitors(
  handler: IntelligenceMonitorHandler,
  options?: SubscriptionOptions
): () => void {
  return client.subscribeMonitor(handler, options);
}

export function sendChatMessage(payload: {
  messages: unknown[];
  ticker?: string;
  profileId?: string;
  storylineContext?: unknown;
  token?: string;
}): void {
  client.sendChatMessage(payload);
}

export function subscribeToChat(handler: ChatEventHandler): () => void {
  return client.subscribeChat(handler);
}
